import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// We now run same-origin, so httpOnly cookies are the single source of truth.
// Keeping a small helper lets us log real errors instead of silently swallowing.
function logAuthError(context, err) {
    if (typeof console !== "undefined" && console.debug) {
        console.debug(`[auth] ${context}:`, err?.message || err);
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = unauth, obj = authed
    const [checking, setChecking] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (err) {
            logAuthError("refresh", err);
            setUser(false);
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
        return data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            logAuthError("logout", err);
        }
        setUser(false);
    }, []);

    return (
        <AuthContext.Provider value={{ user, checking, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
